import { NgModule } from '@angular/core';
import { PreloadAllModules, RouterModule, Routes } from '@angular/router';
import { authGuard, externalLinkTabGuard } from './core/services/auth/auth.guard';
import { orgContextGuard } from './core/services/auth/org-context.guard';
import { orderContextGuard } from './core/services/order/order-context.guard';
import { CHARITY_DETAIL_ROUTE_CONTEXT } from './shared/pages/charity/charity-detail.facade';
import { DONATE_HUB_ROUTE_CONTEXT } from './shared/pages/donate-hub/donate-hub-route-context';
import { UserRoleTabsComponent } from './role-user/user-role-tabs/user-role-tabs.component';
import { CharityRoleTabsComponent } from './role-charity/charity-role-tabs/charity-role-tabs.component';
import { BusinessRoleTabsComponent } from './role-business/business-role-tabs/business-role-tabs.component';

export const routes: Routes = [
  // Auth Pages
  { path: '', redirectTo: 'start', pathMatch: 'full' },
  { path: 'start', loadComponent: () => import('./auth/start/start.page').then(c => c.StartPage) },
  {
    path: 'welcome',
    loadComponent: () => import('./auth/welcome/welcome.page').then(c => c.WelcomePage),
  },
  { path: 'login', loadComponent: () => import('./auth/login/login.page').then(c => c.LoginPage) },
  {
    path: 'signup',
    loadComponent: () => import('./auth/signup/signup.page').then(c => c.SignupPage),
  },
  {
    path: 'verify-email',
    loadComponent: () =>
      import('./auth/verify-email/verify-email.page').then(c => c.VerifyEmailPage),
  },
  {
    path: 'reset-password',
    loadComponent: () =>
      import('./auth/reset-password/reset-password.page').then(c => c.ResetPasswordPage),
  },
  {
    path: 'forgot-password',
    loadComponent: () =>
      import('./auth/forgot-password/forgot-password.page').then(c => c.ForgotPasswordPage),
  },
  {
    path: 'complete-google-profile',
    loadComponent: () =>
      import('./auth/complete-google-profile/complete-google-profile.page').then(c => c.CompleteGoogleProfilePage),
  },
  // User Pages
  {
    path: 'main',
    component: UserRoleTabsComponent,
    children: [
      { path: '', pathMatch: 'full', redirectTo: 'home' },
      {
        path: 'home',
        loadComponent: () => import('./role-user/pages/home/home.page').then(c => c.HomePage),
        canActivate: [authGuard],
      },
      {
        path: 'my-charities',
        loadComponent: () =>
          import('./role-user/pages/my-charities/my-charities.page').then(
            c => c.MyCharitiesPage,
          ),
          canActivate: [authGuard],
          data: { title: 'Charities' }
      },
      {
        path: 'history',
        loadComponent: () =>
          import('./role-user/pages/giving-history/giving-history.page').then(
            c => c.GivingHistoryPage,
          ),
          canActivate: [authGuard],
          data: { title: 'Donation History' }
      },
      {
        path: 'credits',
        loadComponent: () =>
          import('./role-user/pages/credits/credits.page').then(
            c => c.CreditsPage,
          ),
          canActivate: [authGuard],
          data: { title: 'Credits' }
      },
      {
        path: 'categories',
        loadComponent: () =>
          import('./role-user/pages/categories/categories.page').then(m => m.CategoriesPage),
        canActivate: [authGuard],
        data: { title: 'Charity Categories' }
      },
      {
        path: 'network',
        loadComponent: () =>
          import('./role-user/pages/network/network.page').then(m => m.NetworkPage),
        canActivate: [authGuard],
        data: { title: 'Network Impact' }
      },
      {
        path: 'sunny-drives',
        loadComponent: () =>
          import('./role-user/pages/sunny-drives/sunny-drives.page').then(m => m.SunnyDrivesPage),
        data: { title: 'Sunny Drives' }
      },
      {
        path: 'donation-detail/:id',
        loadComponent: () =>
          import('./shared/pages/donation-detail/donation-detail.page').then(c => c.DonationDetailPage),
        data: { portal: 'user' }
      },
      {
        path: 'sunny-drive/:id',
        loadComponent: () =>
          import('./shared/pages/sunny-drive-detail/sunny-drive-detail.page').then(
            c => c.SunnyDriveDetailPage,
          ),
          canActivate: [externalLinkTabGuard],
      },
      {
        path: 'discover',
        loadComponent: () =>
          import('./shared/pages/discover/discover.page').then(c => c.DiscoverPage),
        data: { title: 'Discover' }
      },
      {
        path: 'charity/:id',
        loadComponent: () =>
          import('./shared/pages/charity/charity.page').then(c => c.CharityPage),
        canActivate: [externalLinkTabGuard],
      },
      {
        path: 'business/:id',
        loadComponent: () =>
          import('./shared/pages/business-profile/business-profile.page').then(
            c => c.BusinessProfilePage
          ),
        canActivate: [authGuard, externalLinkTabGuard],
      },
      {
        path: 'user/:id',
        loadComponent: () =>
          import('./shared/pages/user-profile/user-profile.page').then(
            c => c.UserProfilePage
          ),
        canActivate: [authGuard, externalLinkTabGuard],
      },
      {
        path: 'user/:userId/following',
        loadComponent: () =>
          import('./shared/pages/user-following/user-following.page').then(
            c => c.UserFollowingPage
          ),
        canActivate: [externalLinkTabGuard],
      },
      {
        path: 'user/:userId/donations',
        loadComponent: () =>
          import('./shared/pages/user-donations/user-donations.page').then(
            c => c.UserDonationsPage
          ),
        canActivate: [externalLinkTabGuard],
      },
      {
        path: 'charity-social/:id',
        loadComponent: () =>
          import('./role-user/pages/charity-social/charity-social.page').then(
            c => c.CharitySocialPage,
          ),
        data: { title: 'Social Feed' }
      },
      {
        path: 'impact-point/:id',
        loadComponent: () =>
          import('./role-user/pages/impact-point/impact-point.page').then(
            c => c.ImpactPointPage,
          ),
        canActivate: [externalLinkTabGuard],
      },
      {
        path: 'fund-a-need/:id',
        loadComponent: () =>
          import('./role-user/pages/fund-a-need/fund-a-need.page').then(
            c => c.FundANeedPage,
          ),
        canActivate: [externalLinkTabGuard],
      },
      {
        path: 'social',
        loadComponent: () =>
          import('./role-user/pages/social/social.page').then(c => c.SocialPage),
        data: { title: 'Community Feed' }
      },
      {
        path: 'commerce',
        loadComponent: () =>
          import('./role-user/pages/commerce/commerce.page').then(c => c.CommercePage),
        data: { title: 'Marketplace' }
      },
      {
        path: 'donate',
        loadComponent: () =>
          import('./shared/pages/donate-hub/donate-hub.page').then(c => c.DonateHubPage),
        data: { title: 'Donate' }
      },
      {
        path: 'item-detail/:id',
        loadComponent: () =>
          import('./role-user/pages/item-detail/item-detail.page').then(
            c => c.ItemDetailPage,
          ),
        canActivate: [externalLinkTabGuard],
      },
      {
        path: 'auctions/:id',
        loadComponent: () =>
          import('./role-user/pages/auction-profile/auction-profile.page').then(
            c => c.AuctionProfilePage,
          ),
        // canActivate: [externalLinkTabGuard],
      },
      {
        path: 'event/:id',
        loadComponent: () =>
          import('./role-user/pages/event-page/event-page.page').then(
            c => c.EventPage,
          ),
      },
      {
        path: 'event/:eventId/register',
        loadComponent: () =>
          import('./role-user/pages/event-registration/event-registration.page').then(
            c => c.EventRegistrationPage,
          ),
      },
      {
        path: 'user-orders',
        loadComponent: () =>
          import('./shared/pages/orders/orders.page').then(
            c => c.SharedOrdersPage,
          ),
          canActivate: [authGuard],
          data: { role: 'user', title: 'Orders' }
      },
      {
        path: 'user-order-detail/:id',
        loadComponent: () =>
          import('./role-user/pages/order-detail/order-detail.page').then(
            c => c.OrderDetailPage,
          ),
        canActivate: [authGuard],
      },
      {
        path: 'notifications',
        canActivate: [authGuard],
        loadComponent: () =>
          import('./role-user/pages/notifications/notifications.page').then(c => c.NotificationsPage),
        data: { title: 'Notifications' }
      },
      {
        // No authGuard on purpose: guests can win auctions and must be able to pay from here.
        path: 'cart',
        loadComponent: () =>
          import('./role-user/pages/cart/cart.page').then(
            c => c.CartPage,
          ),
      },
      {
        // Old AUCTION_WON notification rows deep-link here forever — the backend URL constant moved
        // to /main/cart, but rows written before that keep the old path (SUN-882).
        path: 'winning-items',
        redirectTo: 'cart',
        pathMatch: 'full',
      },
      {
        path: 'followers/:id',
        loadComponent: () =>
          import('./role-user/pages/followers/followers.page').then(
            c => c.FollowersPage,
          ),
          data: { title: 'Followers' }
      },
      {
        path: 'manage-charity/:id',
        canActivate: [authGuard],
        loadComponent: () =>
          import(
            './shared/pages/claim/claim.page'
          ).then(c => c.SharedClaimPage),
          data: { title: 'Manage Charities', claimMode: 'charity' }
      },
      {
        path: 'search-charity/:id',
        canActivate: [authGuard],
        loadComponent: () =>
          import(
            './role-user/pages/claim-search/claim-search.page'
          ).then(c => c.ClaimSearchPage),
          data: { title: 'Search Charities' }
      },
      {
        path: 'manage-business/:id',
        canActivate: [authGuard],
        loadComponent: () =>
          import(
            './shared/pages/claim/claim.page'
          ).then(c => c.SharedClaimPage),
          data: { title: 'Manage Businesses', claimMode: 'business' }
      },
      {
        path: 'settings/payment-methods',
        canActivate: [authGuard],
        loadComponent: () =>
          import('./role-user/pages/payment-methods/payment-methods.page').then(c => c.PaymentMethodsPage),
        data: { title: 'Payment Methods' }
      },
      {
        path: 'settings/:id',
        canActivate: [authGuard],
        loadComponent: () =>
          import(
            './role-user/pages/settings/settings.page'
          ).then(c => c.SettingsPage),
          data: { title: 'Settings' }
      },
      {
        path: 'event-tickets',
        canActivate: [authGuard],
        loadComponent: () =>
          import('./role-user/pages/event-tickets/event-tickets.page').then(c => c.EventTicketsPage),
        data: { title: 'Event Tickets' }
      },
      {
        path: 'event-ticket/:id',
        canActivate: [authGuard],
        loadComponent: () =>
          import('./role-user/pages/event-ticket-detail/event-ticket-detail.page').then(c => c.EventTicketDetailPage),
        data: { title: 'Ticket Details' }
      },
      {
        path: 'recurring-donations/:id',
        canActivate: [authGuard],
        loadComponent: () =>
          import(
            './role-user/pages/recurring-donations/recurring-donations.page'
          ).then(c => c.RecurringDonationsPage),
          data: { title: 'Recurring Donations' }
      },
      {
        path: 'security/:id',
        canActivate: [authGuard],
        loadComponent: () =>
          import(
            './role-user/pages/security/security.page'
          ).then(c => c.SecurityPage),
          data: { title: 'Security' }
      },
      {
        path: 'support',
        canActivate: [authGuard],
        loadComponent: () =>
          import(
            './role-user/pages/support/support.page'
          ).then(c => c.SupportPage),
          data: { title: 'Support' }
      },
      {
        path: 'user-info/:id',
        canActivate: [authGuard],
        loadComponent: () =>
          import(
            './role-user/pages/user-info/user-info.page'
          ).then(c => c.UserInfoPage),
          data: { title: 'Account Information' }
      },
    ],
  },
  {
    path: 'embed/event/:id',
    loadComponent: () =>
      import('./role-user/pages/event-page/event-page.page').then(
        c => c.EventPage,
      ),
    data: { embedded: true }
  },
  {
    path: 'donation-confirmation',
    // @deprecated Legacy Stripe redirect landing route (SUN-937 Phase 8) — see DonationConfirmationPage's
    // class-level JSDoc for the full deprecation notice. Two remaining callers: BUSINESS-donor checkout
    // (shared donation modal) + PurchaseModalComponent, plus stale PWA bundles. Removal deferred pending
    // the backend BUSINESS-attribution ticket and stale-bundle traffic falling off.
    //canActivate: [authGuard],
    loadComponent: () =>
      import('./shared/pages/donation-confirmation/donation-confirmation.page').then(
        c => c.DonationConfirmationPage,
      ),
  },
  // Charity Pages
  {
    path: 'charity',
    component: CharityRoleTabsComponent,
    canActivate: [authGuard, orgContextGuard],
    // Re-run guards when only query params change: org notification taps switch
    // context via ?charityId= while the portal shell route itself is reused.
    runGuardsAndResolvers: 'paramsOrQueryParamsChange',
    children: [
      { path: '', pathMatch: 'full', redirectTo: 'charity-info' },
      {
        path: 'charity-info',
        loadComponent: () =>
          import('./role-charity/pages/charity-info/charity-info.page').then(
            c => c.CharityInfoPage,
          ),
      },
      {
        path: 'newsfeed',
        loadComponent: () =>
          import('./role-charity/pages/social-content/social-content.page').then(
            c => c.SocialContentPage,
          ),
          data: { title: 'Community Content' }
      },
      {
        path: 'events',
        loadComponent: () =>
          import('./role-charity/pages/events-charity/events-charity.page').then(
            c => c.EventsCharityPage,
          ),
          data: { title: 'Events' }
      },
      {
        path: 'event-detail/:id',
        loadComponent: () =>
          import('./role-charity/pages/event-detail-charity/event-detail-charity.page').then(
            c => c.EventDetailCharityPage,
          ),
      },
      {
        path: 'commerce',
        loadComponent: () =>
          import('./role-charity/pages/commerce-charity/commerce-charity.page').then(
            c => c.CommerceCharityPage,
          ),
          data: { title: 'Marketplace' }
      },
      {
        path: 'marketplace',
        loadComponent: () =>
          import(
            './role-charity/pages/marketplace-charity/marketplace-charity.page'
          ).then(c => c.MarketplaceCharityPage),
          data: { title: 'Buy Now Items' }
      },
      {
        path: 'followers',
        loadComponent: () =>
          import('./role-charity/pages/followers/followers.page').then(
            c => c.FollowersPage,
          ),
          data: { title: 'Followers' }
      },
      {
        path: 'donation-history',
        loadComponent: () =>
          import('./role-charity/pages/donation-history/donation-history.page').then(
            c => c.DonationHistoryPage,
          ),
          data: { title: 'Donation History' }
      },
      {
        path: 'donation-detail/:id',
        loadComponent: () =>
          import('./shared/pages/donation-detail/donation-detail.page').then(c => c.DonationDetailPage),
        data: { portal: 'charity' }
      },
      {
        path: 'auctions',
        loadComponent: () =>
          import('./role-charity/pages/auctions-charity/auctions-charity.page').then(
            c => c.AuctionsCharityPage,
          ),
          data: { title: 'Auctions' }
      },
      {
        path: 'charity-item-detail/:id',
        loadComponent: () =>
          import(
            './role-charity/pages/item-detail-charity/item-detail-charity.page'
          ).then(c => c.ItemDetailCharityPage),
      },
      {
        path: 'fund-a-needs',
        loadComponent: () =>
          import('./role-charity/pages/fund-a-needs/fund-a-needs.page').then(
            c => c.FundANeedsPage,
          ),
          data: { title: 'Fund-A-Need' }
      },
      {
        path: 'impact-point-detail/:id',
        loadComponent: () =>
          import(
            './role-charity/pages/impact-point-detail/impact-point-detail.page'
          ).then(c => c.ImpactPointDetailPage),
      },
      {
        path: 'impact-points',
        loadComponent: () =>
          import('./role-charity/pages/impact-points/impact-points.page').then(
            c => c.ImpactPointsPage,
          ),
          data: { title: 'Impact Points' }
      },
      {
        path: 'fund-a-need-detail/:id',
        loadComponent: () =>
          import(
            './role-charity/pages/fund-a-need-detail/fund-a-need-detail.page'
          ).then(c => c.FundANeedDetailPage),
      },
      {
        path: 'charity-auction-detail/:id',
        loadComponent: () =>
          import(
            './role-charity/pages/auction-detail-charity/auction-detail-charity.page'
          ).then(c => c.AuctionDetailCharityPage),
      },
      {
        path: 'auction-results/:id',
        loadComponent: () =>
          import(
            './role-charity/pages/auction-results/auction-results.page'
          ).then(c => c.AuctionResultsPage),
        data: { title: 'Auction Results' }
      },
      {
        path: 'charity-notifications',
        loadComponent: () =>
          import('./role-charity/pages/notifications-charity/notifications-charity.page').then(
            c => c.NotificationsCharityPage,
          ),
          data: { title: 'Notifications' }
      },
      {
        path: 'charity-orders',
        loadComponent: () =>
          import('./shared/pages/orders/orders.page').then(
            c => c.SharedOrdersPage,
          ),
          data: { role: 'charity', title: 'Orders' }
      },
      {
        path: 'charity-order-detail/:id',
        canActivate: [orderContextGuard],
        loadComponent: () =>
          import(
            './shared/pages/order-detail/order-detail-charity.page'
          ).then(c => c.OrderDetailCharityPage),
      },
      {
        path: 'stripe-account',
        loadComponent: () =>
          import(
            './shared/pages/stripe-management/stripe-management.page'
          ).then(c => c.SharedStripeManagementPage),
          data: { title: 'Stripe Account', stripeMode: 'charity' }
      },
      {
        path: 'recurring-donations',
        canActivate: [authGuard],
        loadComponent: () =>
          import(
            './role-charity/pages/recurring-donations-charity/recurring-donations-charity.page'
          ).then(c => c.RecurringDonationsCharityPage),
          data: { title: 'Recurring Donations' }
      },
      {
        path: 'team-management',
        canActivate: [authGuard],
        loadComponent: () =>
          import('./shared/pages/team-management/team-management.page').then(
            c => c.SharedTeamManagementPage
          ),
        data: { title: 'Team Management', teamMode: 'charity' }
      },
      {
        path: 'settings',
        canActivate: [authGuard],
        loadComponent: () =>
          import('./role-charity/pages/charity-settings/charity-settings.page').then(
            c => c.CharitySettingsPage
          ),
        data: { title: 'Settings' }
      },
      {
        path: 'user/:id',
        loadComponent: () =>
          import('./shared/pages/user-profile/user-profile.page').then(
            c => c.UserProfilePage
          ),
        data: { title: 'User Profile' }
      },
      {
        path: 'user/:userId/following',
        loadComponent: () =>
          import('./shared/pages/user-following/user-following.page').then(
            c => c.UserFollowingPage
          ),
        data: { title: 'Followed Charities' }
      },
      {
        path: 'user/:userId/donations',
        loadComponent: () =>
          import('./shared/pages/user-donations/user-donations.page').then(
            c => c.UserDonationsPage
          ),
        data: { title: 'Donation History' }
      },
      {
        path: 'business/:id',
        loadComponent: () =>
          import('./shared/pages/business-profile/business-profile.page').then(
            c => c.BusinessProfilePage
          ),
        data: { title: 'Business Profile' }
      },
    ],
  },
  {
    path: 'charity-invite',
    loadComponent: () =>
      import('./shared/pages/invite-response/invite-response.page').then(
        c => c.InviteResponsePage
      ),
    data: { inviteMode: 'charity' },
  },
  {
    path: 'business-invite',
    loadComponent: () =>
      import('./shared/pages/invite-response/invite-response.page').then(
        c => c.InviteResponsePage
      ),
    data: { inviteMode: 'business' },
  },
  {
    path: 'auction-donation-invite',
    loadComponent: () =>
      import('./shared/pages/auction-donation-invite/auction-donation-invite.page').then(
        c => c.AuctionDonationInvitePage
      ),
  },
  // Business Pages
  {
    path: 'business',
    component: BusinessRoleTabsComponent,
    canActivate: [authGuard, orgContextGuard],
    // Re-run guards when only query params change: org notification taps switch
    // context via ?businessId= while the portal shell route itself is reused.
    runGuardsAndResolvers: 'paramsOrQueryParamsChange',
    children: [
      { path: '', pathMatch: 'full', redirectTo: 'business-info' },
      {
        path: 'business-info',
        loadComponent: () =>
          import('./role-business/pages/business-info/business-info.page').then(
            c => c.BusinessInfoPage,
          ),
      },
      {
        path: 'settings',
        loadComponent: () =>
          import('./role-business/pages/business-settings/business-settings.page').then(
            c => c.BusinessSettingsPage,
          ),
        data: { title: 'Settings' }
      },
      {
        path: 'social-content',
        loadComponent: () =>
          import('./role-business/pages/social-content-business/social-content-business.page').then(
            c => c.SocialContentBusinessPage,
          ),
          data: { title: 'Social Content' }
      },
      {
        path: 'stripe-account',
        loadComponent: () =>
          import('./shared/pages/stripe-management/stripe-management.page').then(
            c => c.SharedStripeManagementPage,
          ),
          data: { title: 'Stripe Account', stripeMode: 'business' }
      },
      {
        path: 'items',
        loadComponent: () =>
          import('./role-business/pages/items-business/items-business.page').then(
            c => c.ItemsBusinessPage,
          ),
          data: { title: 'Donated Items' }
      },
      {
        path: 'items-grid',
        loadComponent: () =>
          import('./role-business/pages/items-business-grid/items-business-grid.page').then(
            c => c.ItemsBusinessGridPage,
          ),
          data: { title: 'Items' }
      },
      {
        path: 'item-detail/:id',
        loadComponent: () =>
          import('./role-business/pages/item-detail-business/item-detail-business.page').then(
            c => c.ItemDetailBusinessPage,
          ),
          data: { title: 'Item Detail' }
      },
      {
        path: 'auction-detail/:id',
        loadComponent: () =>
          import('./role-business/pages/auction-detail-business/auction-detail-business.page').then(
            c => c.AuctionDetailBusinessPage,
          ),
          data: { title: 'Auction' }
      },
      {
        path: 'orders',
        loadComponent: () =>
          import('./shared/pages/orders/orders.page').then(
            c => c.SharedOrdersPage,
          ),
          data: { role: 'business', title: 'Orders' }
      },
      {
        path: 'order-detail/:id',
        canActivate: [orderContextGuard],
        loadComponent: () =>
          import('./shared/pages/order-detail/order-detail-charity.page').then(
            c => c.OrderDetailCharityPage,
          ),
          data: { title: 'Order Detail' }
      },
      {
        path: 'notifications',
        loadComponent: () =>
          import('./role-business/pages/notifications-business/notifications-business.page').then(
            c => c.NotificationsBusinessPage,
          ),
          data: { title: 'Notifications' }
      },
      {
        path: 'team-management',
        canActivate: [authGuard],
        loadComponent: () =>
          import('./shared/pages/team-management/team-management.page').then(
            c => c.SharedTeamManagementPage
          ),
        data: { title: 'Team Management', teamMode: 'business' }
      },
      {
        path: 'shopify-management',
        canActivate: [authGuard],
        loadComponent: () =>
          import('./role-business/pages/shopify-management/shopify-management.page').then(
            c => c.ShopifyManagementPage
          ),
        data: { title: 'Shopify Management' }
      },
      {
        path: 'sunny-drive/:id',
        loadComponent: () =>
          import('./shared/pages/sunny-drive-detail/sunny-drive-detail.page').then(
            c => c.SunnyDriveDetailPage
          ),
        data: { title: 'Sunny Drive' }
      },
      {
        path: 'donation-history',
        canActivate: [authGuard],
        loadComponent: () =>
          import('./shared/pages/donation-history/donation-history.page').then(
            c => c.DonationHistoryPage
          ),
        data: { title: 'Donation History' }
      },
      {
        path: 'donation-detail/:id',
        loadComponent: () =>
          import('./shared/pages/donation-detail/donation-detail.page').then(
            c => c.DonationDetailPage
          ),
        data: { portal: 'business', title: 'Donation Detail' }
      },
      {
        path: 'discover',
        loadComponent: () =>
          import('./shared/pages/discover/discover.page').then(
            c => c.DiscoverPage
          ),
        data: { title: 'Discover Charities' }
      },
      {
        path: 'donate',
        loadComponent: () =>
          import('./shared/pages/donate-hub/donate-hub.page').then(c => c.DonateHubPage),
        providers: [
          { provide: DONATE_HUB_ROUTE_CONTEXT, useValue: 'business' as const },
        ],
        data: { title: 'Donate' }
      },
      {
        path: 'recurring-donations',
        loadComponent: () =>
          import('./role-business/pages/recurring-donations-business/recurring-donations-business.page').then(
            c => c.RecurringDonationsBusinessPage,
          ),
        data: { title: 'Recurring Donations' }
      },
      {
        path: 'charity/:id',
        loadComponent: () =>
          import('./shared/pages/charity/charity.page').then(
            c => c.CharityPage
          ),
        providers: [
          { provide: CHARITY_DETAIL_ROUTE_CONTEXT, useValue: 'business' as const },
        ],
        data: { title: 'Charity' }
      },
      {
        path: 'user/:id',
        loadComponent: () =>
          import('./shared/pages/user-profile/user-profile.page').then(
            c => c.UserProfilePage
          ),
        data: { title: 'User Profile' }
      },
      {
        path: 'user/:userId/following',
        loadComponent: () =>
          import('./shared/pages/user-following/user-following.page').then(
            c => c.UserFollowingPage
          ),
        data: { title: 'Followed Charities' }
      },
      {
        path: 'user/:userId/donations',
        loadComponent: () =>
          import('./shared/pages/user-donations/user-donations.page').then(
            c => c.UserDonationsPage
          ),
        data: { title: 'Donation History' }
      },
      {
        path: 'business/:id',
        loadComponent: () =>
          import('./shared/pages/business-profile/business-profile.page').then(
            c => c.BusinessProfilePage
          ),
        data: { title: 'Business Profile' }
      },
    ],
  },
  {
    path: '**',
    redirectTo: '',
    pathMatch: 'full',
  },
];
@NgModule({
  imports: [
    RouterModule.forRoot(routes, {
      preloadingStrategy: PreloadAllModules,
      onSameUrlNavigation: 'reload',
    }),
  ],
  exports: [RouterModule],
})
export class AppRoutingModule {}
